import { useEffect, useState } from "react";
import { Link, NavLink, Outlet, useNavigate } from "react-router-dom";
import { useAuth } from "@/AuthContext";
import LangToggle from "@/components/LangToggle";
import ThemeToggle from "@/components/ThemeToggle";
import { LayoutDashboard, Users, FilePlus2, Settings, LogOut, ShieldCheck } from "lucide-react";

export default function DashboardLayout() {
  const { user, loading, logout, t, theme } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (!loading && !user) navigate("/login", { replace: true });
  }, [user, loading, navigate]);

  if (loading || !user) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-bone">
        <div className="text-ink-muted">{t.common.loading}</div>
      </div>
    );
  }

  const links = [
    { to: "/dashboard", label: t.nav.dashboard, icon: LayoutDashboard, end: true },
    { to: "/dashboard/patients", label: t.nav.patients, icon: Users },
    { to: "/dashboard/sessions/new", label: t.nav.newSession, icon: FilePlus2 },
    { to: "/dashboard/settings", label: t.nav.settings, icon: Settings },
  ];

  return (
    <div className={`min-h-screen bg-bone flex ${theme === "dark" ? "dark" : ""}`}>
      {/* Sidebar */}
      <aside className="hidden md:flex md:flex-col w-64 bg-surface border-r border-hairline">
        <div className="h-16 flex items-center gap-2 px-6 border-b border-hairline">
          <div className="w-8 h-8 rounded-lg bg-pine flex items-center justify-center text-bone font-heading font-semibold">P</div>
          <Link to="/" className="font-heading tracking-tight text-lg">PhysioSOAP</Link>
        </div>
        <nav className="flex-1 p-4 space-y-1">
          {links.map((l) => (
            <NavLink
              key={l.to} to={l.to} end={l.end}
              data-testid={`nav-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
              className={({ isActive }) =>
                `flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm transition ${
                  isActive ? "bg-pine-50 text-pine font-semibold" : "text-ink-muted hover:bg-pine-50/60 hover:text-ink"
                }`
              }
            >
              <l.icon className="w-4 h-4" strokeWidth={1.75} /> {l.label}
            </NavLink>
          ))}
          {user?.is_admin && (
            <NavLink
              to="/admin"
              data-testid="nav-admin-center"
              className={({ isActive }) =>
                `mt-4 flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm transition border ${
                  isActive
                    ? "bg-terracotta text-bone border-terracotta font-semibold"
                    : "bg-terracotta/10 text-terracotta border-terracotta/20 hover:bg-terracotta/15"
                }`
              }
            >
              <ShieldCheck className="w-4 h-4" strokeWidth={1.75} /> Admin-Zentrale
            </NavLink>
          )}
        </nav>
        <div className="p-4 border-t border-hairline">
          <div className="flex items-center gap-3 px-2 py-2">
            {user.picture ? (
              <img src={user.picture} alt="" className="w-9 h-9 rounded-full object-cover" />
            ) : (
              <div className="w-9 h-9 rounded-full bg-pine-50 text-pine flex items-center justify-center font-semibold">{user.name?.[0]}</div>
            )}
            <div className="flex-1 min-w-0">
              <div className="text-sm font-medium truncate">{user.name}</div>
              <div className="text-xs text-ink-muted truncate">{user.email}</div>
            </div>
          </div>
          <button
            onClick={logout}
            className="mt-2 w-full inline-flex items-center justify-center gap-2 px-3 py-2 rounded-lg text-sm text-ink-muted hover:bg-pine-50 hover:text-ink transition"
            data-testid="logout-btn"
          >
            <LogOut className="w-4 h-4" /> {t.nav.logout}
          </button>
        </div>
      </aside>

      {/* Main */}
      <main className="flex-1 flex flex-col min-w-0">
        <header className="h-16 bg-surface border-b border-hairline flex items-center justify-between px-6">
          <MobileNav links={links} />
          <div className="flex items-center gap-3 ml-auto">
            <ThemeToggle />
            <LangToggle />
          </div>
        </header>
        <div className="flex-1 p-6 md:p-8">
          <Outlet />
        </div>
      </main>
    </div>
  );
}

function MobileNav({ links }) {
  const [open, setOpen] = useState(false);
  const { user } = useAuth();
  return (
    <div className="md:hidden">
      <button onClick={() => setOpen(!open)} className="p-2 rounded-lg hover:bg-pine-50">
        <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M3 6h18M3 12h18M3 18h18"/></svg>
      </button>
      {open && (
        <div className="absolute top-16 left-0 right-0 bg-surface border-b border-hairline p-4 z-30">
          {user?.is_admin && (
            <NavLink to="/admin" onClick={() => setOpen(false)}
              className="block px-3 py-2 rounded-lg bg-terracotta/10 text-terracotta font-semibold mb-2"
              data-testid="mobile-nav-admin"
            >Admin-Zentrale</NavLink>
          )}
          {links.map((l) => (
            <NavLink key={l.to} to={l.to} end={l.end} onClick={() => setOpen(false)}
              className={({ isActive }) => `block px-3 py-2 rounded-lg ${isActive ? "bg-pine-50 text-pine font-semibold" : "text-ink-muted"}`}
            >{l.label}</NavLink>
          ))}
        </div>
      )}
    </div>
  );
}
